import { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Package, Mail, Copy, Clock, X, Send, Loader2 } from "lucide-react";

const PIPELINE = [
  { value: "request_received", label: "Request Received" },
  { value: "production_invoice_sent", label: "Production Invoice Sent" },
  { value: "production_paid", label: "Production Paid" },
  { value: "production_scheduled", label: "Production Scheduled" },
  { value: "in_production", label: "In Production" },
  { value: "quality_review", label: "Quality Review" },
  { value: "shipping_invoice_sent", label: "Shipping Invoice Sent" },
  { value: "shipping_paid", label: "Shipping Paid" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "closed", label: "Closed" },
];

const STAGE_COLORS = {
  request_received: "#c8a24a", production_invoice_sent: "#946600",
  production_paid: "#4a7c3e", production_scheduled: "#3d7f9c",
  in_production: "#2f5f3f", quality_review: "#5a5a5a",
  shipping_invoice_sent: "#946600", shipping_paid: "#4a7c3e",
  shipped: "#1a4a1a", delivered: "#1a4a1a", closed: "#666", draft: "#999",
};

const AdminReorderRequests = () => {
  const [reorders, setReorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (search) params.set("q", search);
    api.get(`/admin/reorder-requests?${params.toString()}`)
      .then((r) => setReorders(r.data.reorders || []))
      .catch(() => setReorders([]))
      .finally(() => setLoading(false));
  }, [status, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    reorders.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [reorders]);

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/reorder-requests/${id}`, patch);
      setReorders((list) => list.map((x) => x.id === id ? data.reorder : x));
      if (selected?.id === id) setSelected(data.reorder);
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="ft-eyebrow mb-2">Client Reorders</div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-reorders-title">Command Center · Reorder Requests</h1>
        <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
          Every pallet reorder from a client, moving through the 11-stage production + shipping
          pipeline. Update status inline to move a reorder forward.
        </p>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="admin-reorders-pipeline">
        <button
          onClick={() => setStatus("")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            status === "" ? "bg-[var(--ft-black)] text-white" : "bg-[var(--ft-light-gray)] text-[var(--ft-charcoal)] hover:bg-[var(--ft-gold-soft)]"
          }`}
        >All ({reorders.length})</button>
        {PIPELINE.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatus(s.value === status ? "" : s.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              status === s.value ? "text-white" : "bg-[var(--ft-light-gray)] text-[var(--ft-charcoal)] hover:bg-[var(--ft-gold-soft)]"
            }`}
            style={status === s.value ? { background: STAGE_COLORS[s.value] } : {}}
            data-testid={`reorder-pipeline-${s.value}`}
          >
            {s.label} ({counts[s.value] || 0})
          </button>
        ))}
      </div>

      <div className="ft-card !p-3 flex items-center gap-3 mb-4">
        <Search size={18} className="text-[var(--ft-muted)] ml-1" />
        <input className="flex-1 outline-none bg-transparent text-sm" placeholder="Search client, email, company, brand…"
               value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-reorders-search" />
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : reorders.length === 0 ? (
        <div className="ft-card text-center py-16"><Package size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" /><h3 className="text-lg font-semibold">No reorder requests match.</h3></div>
      ) : (
        <div className="ft-card !p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-reorders-table">
            <thead className="bg-[var(--ft-light-gray)] text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-[var(--ft-charcoal)]">
              <tr>
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Brand</th>
                <th className="text-left p-4">Pallets</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Submitted</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reorders.map((r) => (
                <tr key={r.id} className="border-t border-[var(--ft-border)] hover:bg-[var(--ft-light-gray)] cursor-pointer" onClick={() => setSelected(r)} data-testid={`reorder-row-${r.id}`}>
                  <td className="p-4">
                    <div className="font-semibold">{r.user_name || r.user_email}</div>
                    <div className="text-xs text-[var(--ft-muted)] mt-0.5">{r.company || r.user_email}</div>
                  </td>
                  <td className="p-4 text-sm">{r.brand_name}</td>
                  <td className="p-4 text-sm">{r.pallets}</td>
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    <select value={r.status} onChange={(e) => update(r.id, { status: e.target.value })}
                            className="text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer"
                            style={{ background: STAGE_COLORS[r.status] || "#666", color: "#fff" }}
                            data-testid={`reorder-status-${r.id}`}>
                      {PIPELINE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-xs text-[var(--ft-muted)]">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <a href={`mailto:${r.user_email}?subject=Your FLOWTALIS reorder — ${r.brand_name}`} title="Email client"
                         className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center"><Mail size={14} /></a>
                      <button onClick={() => { navigator.clipboard.writeText(r.user_email); toast.success("Copied"); }} title="Copy email"
                              className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center"><Copy size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <Drawer reorder={selected} onClose={() => setSelected(null)} onUpdate={update} />}
    </AdminLayout>
  );
};

const Drawer = ({ reorder, onClose, onUpdate }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    await onUpdate(reorder.id, { note });
    setNote("");
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-reorder-drawer">
        <div className="p-6 border-b border-[var(--ft-border)] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="ft-eyebrow">Reorder Detail</div>
            <h2 className="text-xl font-bold mt-1">{reorder.brand_name} · {reorder.pallets} pallet(s)</h2>
            <p className="text-xs text-[var(--ft-muted)] mt-0.5">Submitted {new Date(reorder.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="h-10 w-10 rounded-lg hover:bg-[var(--ft-light-gray)] flex items-center justify-center"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-[var(--ft-gold)] mb-3">Client</div>
            <KV k="Name" v={reorder.user_name} />
            <KV k="Email" v={<a href={`mailto:${reorder.user_email}`} className="text-[var(--ft-gold)] hover:underline">{reorder.user_email}</a>} />
            <KV k="Company" v={reorder.company || "—"} />
            <KV k="Phone" v={reorder.phone || "—"} />
          </div>
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-[var(--ft-gold)] mb-3">Order</div>
            <KV k="Brand" v={reorder.brand_name} />
            <KV k="Beverage Type" v={reorder.beverage_type || "—"} />
            <KV k="Pallets" v={reorder.pallets} />
            {reorder.additional_notes && <KV k="Client Notes" v={<span className="whitespace-pre-wrap">{reorder.additional_notes}</span>} />}
          </div>
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-[var(--ft-gold)] mb-3">Add Note</div>
            <textarea className="ft-input min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Sent production invoice, client confirmed shipping address…"
                      data-testid="admin-reorder-note-input" />
            <button onClick={addNote} disabled={saving || !note.trim()} className="ft-btn-primary mt-3" data-testid="admin-reorder-note-submit">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Add Note
            </button>
          </div>
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] font-bold text-[var(--ft-gold)] mb-3">Activity Timeline</div>
            <ol className="space-y-3">
              {[...(reorder.activity || [])].reverse().map((a, i) => (
                <li key={i} className="flex gap-3">
                  <div className="mt-1.5 h-2 w-2 rounded-full bg-[var(--ft-gold)] shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs text-[var(--ft-muted)] flex items-center gap-2">
                      <Clock size={11} /> {new Date(a.at).toLocaleString()} · <span className="font-semibold text-[var(--ft-charcoal)]">{a.actor}</span>
                    </div>
                    <div className="text-sm text-[var(--ft-charcoal)] mt-0.5 whitespace-pre-wrap">{a.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-sm py-1.5 border-b border-[var(--ft-border)] last:border-0">
    <span className="text-[var(--ft-muted)] uppercase text-[0.7rem] tracking-[0.16em] font-semibold shrink-0">{k}</span>
    <span className="text-right text-[var(--ft-charcoal)] max-w-[65%] break-words">{v}</span>
  </div>
);

export default AdminReorderRequests;
